import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface DiscoverProductsProps {
  t: any;
  isRTL: boolean;
}

const DiscoverSection = styled.section`
  min-height: 80vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  z-index: 2;
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  color: #0f172a;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ViewAllLink = styled(motion.a)<{ $isRTL: boolean }>`
  color: #dc2626;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    color: #b91c1c;
    transform: translateX(${props => props.$isRTL ? '-4px' : '4px'});
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ProductsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ProductCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #f1f5f9;
  position: relative;
  z-index: 1;
  
  &:hover {
    box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
    border-color: #e2e8f0;
  }
  
  @media (max-width: 768px) {
    border-radius: 12px;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px -1px rgba(0, 0, 0, 0.15);
    }
  }
`;

const ProductImage = styled.div`
  height: 200px;
  width: 100%;
  overflow: hidden;
  position: relative;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    border-radius: 8px;
  }
  
  ${ProductCard}:hover & img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 180px;
    padding: 0.75rem;
  }
`;

const ProductContent = styled.div<{ $isRTL: boolean }>`
  padding: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ProductTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.4;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ShowMoreButton = styled(motion.button)<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  border: none;
  color: white;
  padding: 0.8rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 3rem;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.5rem;
    font-size: 0.9rem;
    margin-top: 2rem;
  }
`;

const DiscoverProducts: React.FC<DiscoverProductsProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const navigate = useNavigate();
  const [showAllProducts, setShowAllProducts] = useState(false);

  const initialProductCategories = [
    {
      id: 'postes-prefabriques',
      title: t.prefabricatedSubstationsTitle,
      image: '/images/catalog/Postes préfabriqués.jpg'
    },
    {
      id: 'transformateurs',
      title: t.transformersTitle,
      image: '/images/catalog/transformateurs.jpg'
    },
    {
      id: 'cellules-hta',
      title: t.switchgearTitle,
      image: '/images/catalog/Cellule HTA.jpg'
    },
    {
      id: 'cables-transport',
      title: t.cableTransportTitle,
      image: '/images/catalog/Câbles de transport et de distribution de l\'énergie.jpg'
    }
  ];

  const additionalProductCategories = [
    {
      id: 'materiel-electrique',
      title: 'Matériel électrique',
      image: '/images/products/IACM 50.png'
    },
    {
      id: 'cables-batiments',
      title: t.cableBuildingsTitle,
      image: '/images/catalog/Câbles de bâtiments.jpg'
    },
    {
      id: 'groupes-electrogenes',
      title: 'Groupes électrogènes',
      image: '/images/products/Groupe electrogene.png'
    },
    {
      id: 'eclairage-public',
      title: t.publicLightingTitle,
      image: '/images/plights.png'
    }
  ];

  const allProductCategories = showAllProducts 
    ? [...initialProductCategories, ...additionalProductCategories] 
    : initialProductCategories;

  const handleProductClick = (categoryId: string) => {
    navigate(`/category/${categoryId}`);
  };

  return (
    <DiscoverSection id="discover-products" ref={ref}>
      <Container $isRTL={isRTL}>
        <SectionHeader>
          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t.discoverProductsTitle}
          </SectionTitle>
          <ViewAllLink 
            $isRTL={isRTL}
            href="/catalogue"
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: isRTL ? 30 : -30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t.viewAllProducts} {isRTL ? '←' : '→'}
          </ViewAllLink>
        </SectionHeader>
        
        <ProductsGrid
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{
            visible: { transition: { staggerChildren: 0.1 } },
            hidden: {  },
          }}
        >
          {allProductCategories.map((product, index) => (
            <ProductCard
              key={product.id}
              $isRTL={isRTL}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              onClick={() => handleProductClick(product.id)}
            >
              <ProductImage>
                <img
                  src={product.image}
                  alt={product.title}
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/products/default-product.jpg';
                  }}
                />
              </ProductImage>
              <ProductContent $isRTL={isRTL}>
                <ProductTitle $isRTL={isRTL}>
                  {product.title}
                </ProductTitle>
              </ProductContent>
            </ProductCard>
          ))}
        </ProductsGrid>

        {!showAllProducts && (
          <ShowMoreButton
            $isRTL={isRTL}
            onClick={() => setShowAllProducts(true)}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
{t.showMore}
          </ShowMoreButton>
        )}
      </Container>
    </DiscoverSection>
  );
};

export default DiscoverProducts;
